import { useMemo, useState, type ReactElement } from 'react';
import type { BoardInviteLink, GrantableBoardRole } from '@molt/shared';
import { Button, Dialog, Notice, TextField } from '@/components';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';

export interface BoardInviteLinkSectionProps {
  link: BoardInviteLink | null | undefined;
  loading?: boolean | undefined;
  canManage: boolean;
  disabledReason: string | null;
  createBusy?: boolean | undefined;
  revokeBusy?: boolean | undefined;
  createError?: string | undefined;
  revokeError?: string | undefined;
  origin: string;
  showHeading?: boolean | undefined;
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents -- Fixed by P2-28-BRIEF.md §6.
  onCreate: (role?: GrantableBoardRole | undefined) => void;
  onRevoke: () => void;
}

/**
 * ⚠ A FUNCTION OF `t`, where this was a module-level `const` record of literals —
 * a module constant cannot call `t`. Written in `BoardCopyDialog`'s
 * `visibilityChoices` shape rather than the `ROLE_LABEL_KEYS` table shape, so
 * each value stays a literal `t('…')` call and all three keys remain visible to
 * the `t('…')` anchor the routing pin counts with. No rendering assertion in
 * `BoardInviteLinkSection.test.tsx` reaches the hint these three words appear
 * in, so the anchor is the only proof they resolve.
 *
 * These are the lowercase mid-sentence forms, read only by `invite.urlHint`.
 * The capitalised standalone role words are `boardMembers.role.option.*` and
 * belong to another slice.
 */
function roleLabels(
  t: InterfaceLanguageContextValue['t'],
): Record<BoardInviteLink['roleGranted'], string> {
  return {
    admin: t('boardAdmin.invite.role.admin'),
    normal: t('boardAdmin.invite.role.normal'),
    observer: t('boardAdmin.invite.role.observer'),
  };
}

function inviteUrl(origin: string, token: string): string {
  return `${origin.replace(/\/+$/, '')}/board-invites/${encodeURIComponent(token)}`;
}

function controlDisabledReason(
  t: InterfaceLanguageContextValue['t'],
  checking: boolean,
  canManage: boolean,
  disabledReason: string | null,
): string | null {
  if (checking) return t('boardAdmin.invite.checking');
  if (canManage) return null;
  return disabledReason ?? t('boardAdmin.invite.reason.cannotManage');
}

export function BoardInviteLinkSection({
  link,
  loading = false,
  canManage,
  disabledReason,
  createBusy,
  revokeBusy,
  createError,
  revokeError,
  origin,
  showHeading = true,
  onCreate,
  onRevoke,
}: BoardInviteLinkSectionProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const checking = loading || link === undefined;
  const lockedReason = controlDisabledReason(t, checking, canManage, disabledReason);
  const activeUrl = useMemo(
    () => (link === null || link === undefined ? null : inviteUrl(origin, link.token)),
    [link, origin],
  );
  const activeRoleLabel =
    link === null || link === undefined ? null : roleLabels(t)[link.roleGranted];
  const clipboardAvailable =
    typeof navigator !== 'undefined' && navigator.clipboard?.writeText !== undefined;
  const copyDisabledReason =
    lockedReason ?? (clipboardAvailable ? null : t('boardAdmin.invite.reason.clipboard'));

  async function copyLink(): Promise<void> {
    if (activeUrl === null || copyDisabledReason !== null || !clipboardAvailable) return;

    try {
      await navigator.clipboard.writeText(activeUrl);
      setCopyError(null);
      setCopyMessage(t('boardAdmin.invite.copied'));
    } catch {
      setCopyMessage(null);
      setCopyError(t('boardAdmin.invite.copyFailed'));
    }
  }

  function generate(role?: GrantableBoardRole): void {
    setCopyMessage(null);
    setCopyError(null);
    if (role === undefined) {
      onCreate();
      return;
    }
    onCreate(role);
  }

  function confirmRevoke(): void {
    if (revokeBusy === true) return;
    setCopyMessage(null);
    setCopyError(null);
    onRevoke();
    setConfirmOpen(false);
  }

  const createBusyProps = createBusy === undefined ? {} : { busy: createBusy };
  const revokeBusyProps = revokeBusy === undefined ? {} : { busy: revokeBusy };
  const lockProps =
    lockedReason === null ? {} : { disabled: true, disabledReason: lockedReason };
  const copyLockProps =
    copyDisabledReason === null
      ? {}
      : { disabled: true, disabledReason: copyDisabledReason };

  return (
    <section
      {...(showHeading
        ? { 'aria-labelledby': 'board-invite-link-heading' }
        : { 'aria-label': t('boardAdmin.invite.heading') })}
      className="flex flex-col gap-4"
    >
      <div className="flex flex-col gap-1">
        {showHeading ? (
          <h2 id="board-invite-link-heading" className="text-base font-semibold text-ink">
            {t('boardAdmin.invite.heading')}
          </h2>
        ) : null}
        <p className="text-sm text-ink-muted">{t('boardAdmin.invite.description')}</p>
      </div>

      {checking ? (
        <Notice tone="info" body={t('boardAdmin.invite.loading')} />
      ) : activeUrl === null ? (
        <div className="rounded-card border border-border-strong bg-surface-raised p-3 text-sm text-ink-muted">
          {t('boardAdmin.invite.none')}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <TextField
            label={t('boardAdmin.invite.urlLabel')}
            readOnly
            value={activeUrl}
            /* `String(...)` rather than `?? ''`: this branch is only reached with a
               link in hand, so `activeRoleLabel` is never null here, and `String`
               reproduces the template literal this replaced byte for byte if it
               ever were. */
            hint={t('boardAdmin.invite.urlHint', { role: String(activeRoleLabel) })}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={() => {
                void copyLink();
              }}
              {...copyLockProps}
            >
              {t('boardAdmin.invite.copy')}
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={() => setConfirmOpen(true)}
              {...revokeBusyProps}
              {...lockProps}
            >
              {t('boardAdmin.invite.revoke')}
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="primary"
          onClick={() => generate()}
          {...createBusyProps}
          {...lockProps}
        >
          {t('boardAdmin.invite.generate')}
        </Button>
        <Button
          type="button"
          onClick={() => generate('observer')}
          {...createBusyProps}
          {...lockProps}
        >
          {t('boardAdmin.invite.generateObserver')}
        </Button>
      </div>

      {createError !== undefined ? <Notice tone="bad" body={createError} /> : null}
      {revokeError !== undefined ? <Notice tone="bad" body={revokeError} /> : null}
      {copyMessage !== null ? <Notice tone="info" body={copyMessage} /> : null}
      {copyError !== null ? <Notice tone="bad" body={copyError} /> : null}

      <Dialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        size="sm"
        title={t('boardAdmin.invite.revokeDialog.title')}
        description={t('boardAdmin.invite.revokeDialog.description')}
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setConfirmOpen(false)}
            >
              {t('boardAdmin.invite.revokeDialog.cancel')}
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={confirmRevoke}
              {...revokeBusyProps}
            >
              {t('boardAdmin.invite.revokeDialog.confirm')}
            </Button>
          </>
        }
      >
        {activeUrl === null ? (
          <Notice tone="info" body={t('boardAdmin.invite.revokeDialog.none')} />
        ) : (
          <p className="text-sm text-ink-muted">
            {t('boardAdmin.invite.revokeDialog.note')}
          </p>
        )}
      </Dialog>
    </section>
  );
}
